import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, Save, Settings2, ShieldCheck, Activity } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  return (
    <Tabs defaultValue="hsn" className="space-y-4">
      <TabsList className="bg-white border border-slate-200 h-9 p-1">
        <TabsTrigger value="hsn" data-testid="tab-hsn" className="text-[12px] gap-1.5"><ShieldCheck size={13} />HSN Cache</TabsTrigger>
        <TabsTrigger value="tolerance" data-testid="tab-tolerance" className="text-[12px] gap-1.5"><Settings2 size={13} />Tolerance Rules</TabsTrigger>
        <TabsTrigger value="sap" data-testid="tab-sap" className="text-[12px] gap-1.5"><Activity size={13} />SAP Config</TabsTrigger>
      </TabsList>
      <TabsContent value="hsn"><HSNTab /></TabsContent>
      <TabsContent value="tolerance"><ToleranceTab /></TabsContent>
      <TabsContent value="sap"><SAPConfigTab /></TabsContent>
    </Tabs>
  );
}

/* ── HSN Cache ───────────────────────────────────────────────── */
function HSNTab() {
  const [codes, setCodes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({ hsn_code: "", description: "", cgst_rate: 9, sgst_rate: 9, igst_rate: 18, uom: "KGM", effective_date: "2024-01-01" });
  const [saving, setSaving]     = useState(false);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("/hsn");
      setCodes(data.hsn_codes || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openCreate = () => {
    setEditing(null);
    setForm({ hsn_code: "", description: "", cgst_rate: 9, sgst_rate: 9, igst_rate: 18, uom: "KGM", effective_date: "2024-01-01" });
    setShowForm(true);
  };

  const openEdit = (code) => {
    setEditing(code.hsn_code);
    setForm({ hsn_code: code.hsn_code, description: code.description, cgst_rate: code.cgst_rate, sgst_rate: code.sgst_rate, igst_rate: code.igst_rate, uom: code.uom, effective_date: code.effective_date });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await apiPut(`/hsn/${editing}`, form);
        toast.success("HSN code updated");
      } else {
        await apiPost("/hsn", form);
        toast.success("HSN code added");
      }
      setShowForm(false);
      fetchCodes();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (code) => {
    try {
      await apiDelete(`/hsn/${code}`);
      toast.success("Deactivated");
      fetchCodes();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-[13px] text-slate-600">{codes.length} HSN codes in cache</p>
        <Button onClick={openCreate} data-testid="add-hsn-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
          <Plus size={14} /> Add HSN Code
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-200 border-b-2 border-slate-300">
              {["HSN Code", "Description", "UOM", "CGST %", "SGST %", "IGST %", "Active", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-bold text-slate-700 uppercase text-[11px] tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-300">
            {loading ? (
              <tr><td colSpan={8} className="py-8 text-center"><Loader2 size={16} className="animate-spin mx-auto text-slate-400" /></td></tr>
            ) : codes.map((c) => (
              <tr key={c.hsn_code} className="hover:bg-slate-50" data-testid={`hsn-row-${c.hsn_code}`}>
                <td className="px-4 py-3 font-mono font-medium text-slate-900">{c.hsn_code}</td>
                <td className="px-4 py-3 text-slate-700">{c.description}</td>
                <td className="px-4 py-3 font-mono text-slate-600">{c.uom}</td>
                <td className="px-4 py-3 font-mono">{c.cgst_rate}%</td>
                <td className="px-4 py-3 font-mono">{c.sgst_rate}%</td>
                <td className="px-4 py-3 font-mono">{c.igst_rate}%</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded-full ${c.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {c.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-6 w-6 p-0 text-slate-400" data-testid={`edit-hsn-${c.hsn_code}`}>
                    <Pencil size={11} />
                  </Button>
                  {c.is_active && (
                    <Button variant="ghost" size="sm" onClick={() => handleDeactivate(c.hsn_code)} className="h-6 w-6 p-0 text-red-400 hover:text-red-600" data-testid={`delete-hsn-${c.hsn_code}`}>
                      <Trash2 size={11} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">{editing ? "Edit HSN Code" : "Add HSN Code"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <FieldRow label="HSN Code">
              <Input value={form.hsn_code} onChange={(e) => setForm(f => ({ ...f, hsn_code: e.target.value }))}
                disabled={!!editing} className="text-sm font-mono border-slate-300" data-testid="hsn-code-input" />
            </FieldRow>
            <FieldRow label="Description">
              <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="text-sm border-slate-300" data-testid="hsn-description-input" />
            </FieldRow>
            <FieldRow label="UOM">
              <Select value={form.uom} onValueChange={(v) => setForm(f => ({ ...f, uom: v }))}>
                <SelectTrigger className="border-slate-300 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["KGM", "EA", "MT", "L", "M", "M2", "M3"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldRow>
            <div className="grid grid-cols-3 gap-2">
              {[["CGST %", "cgst_rate"], ["SGST %", "sgst_rate"], ["IGST %", "igst_rate"]].map(([label, field]) => (
                <FieldRow key={field} label={label}>
                  <Input type="number" min="0" max="30" value={form[field]} onChange={(e) => setForm(f => ({ ...f, [field]: parseFloat(e.target.value) || 0 }))}
                    className="text-sm border-slate-300 font-mono" data-testid={`hsn-${field}`} />
                </FieldRow>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-[13px]">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-hsn-button"
                className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {editing ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Tolerance Rules ────────────────────────────────────────── */
function ToleranceTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [tolerancePct, setTolerancePct] = useState("1.0");
  const [defaultTerms, setDefaultTerms] = useState("NET30");

  useEffect(() => {
    apiGet("/settings").then((d) => {
      setSettings(d);
      setTolerancePct(String(d.tolerance_pct ?? 1.0));
      setDefaultTerms(d.default_payment_terms || "NET30");
      setLoading(false);
    }).catch((e) => { toast.error(e.message); setLoading(false); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiPut("/settings", { tolerance_pct: parseFloat(tolerancePct), default_payment_terms: defaultTerms });
      toast.success("Settings saved");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-md space-y-5 bg-white border border-slate-200 rounded-lg p-6">
      <div className="space-y-1.5">
        <label className="text-[12px] font-medium text-slate-700">Customs Duty Tolerance % (Hard Block)</label>
        <Input
          type="number" min="0" max="10" step="0.1"
          value={tolerancePct} onChange={(e) => setTolerancePct(e.target.value)}
          className="text-sm border-slate-300 font-mono max-w-xs"
          data-testid="tolerance-input"
        />
        <p className="text-[11px] text-slate-400">Reconciliation will be blocked if entered duty differs from paid by more than this %</p>
      </div>
      <div className="space-y-1.5">
        <label className="text-[12px] font-medium text-slate-700">Default Payment Terms</label>
        <Select value={defaultTerms} onValueChange={setDefaultTerms}>
          <SelectTrigger className="border-slate-300 text-sm max-w-xs" data-testid="default-terms-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["NET30", "NET45", "NET60", "IMMEDIATE", "ADVANCE"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button onClick={save} disabled={saving} data-testid="save-tolerance-button"
        className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
        Save Settings
      </Button>
    </div>
  );
}

/* ── SAP Config ────────────────────────────────────────────── */
function SAPConfigTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    apiGet("/settings").then((d) => { setSettings(d); setLoading(false); })
      .catch((e) => { toast.error(e.message); setLoading(false); });
  }, []);

  const toggleMock = async () => {
    setSaving(true);
    try {
      const updated = await apiPut("/settings", { sap_mock_mode: !settings.sap_mock_mode });
      setSettings(updated);
      toast.success(updated.sap_mock_mode ? "SAP Mock Mode ON" : "SAP Live Mode ON");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-lg space-y-5 bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
        <div>
          <p className="text-[13px] font-medium text-slate-900">SAP Mock Mode</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {settings?.sap_mock_mode ? "All SAP calls return mock responses. Safe for testing." : "SAP calls hit live ByDesign endpoints."}
          </p>
        </div>
        <button
          onClick={toggleMock}
          disabled={saving}
          data-testid="sap-mock-toggle"
          className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${settings?.sap_mock_mode ? "bg-[#0052CC]" : "bg-slate-300"}`}
        >
          <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow-sm ${settings?.sap_mock_mode ? "left-6" : "left-1"}`} />
        </button>
      </div>

      {[
        ["SAP Instance", settings?.materials_hub_api_url || "—"],
      ].map(([label, val]) => (
        <div key={label} className="space-y-1">
          <p className="text-[11px] font-mono uppercase text-slate-400">{label}</p>
          <p className="text-[12px] font-mono text-slate-700 break-all">{val}</p>
        </div>
      ))}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[12px] font-medium text-slate-700">{label}</label>
      {children}
    </div>
  );
}
